import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { UserPlus, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AssetAllocation({ user, onLogout }) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    asset_id: '',
    employee_id: '',
    allocation_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchAvailableAssets();
    fetchEmployees();
  }, []);

  const fetchAvailableAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assets?status=Available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assets/allocate`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Asset allocated successfully!');
      setFormData({
        asset_id: '',
        employee_id: '',
        allocation_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchAvailableAssets();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Asset Allocation</h1>
          <p className="text-gray-600 mt-2">Assign assets to employees</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Asset Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Select Asset *</label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => setFormData({...formData, asset_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Choose an available asset</option>
                  {assets.map(asset => (
                    <option key={asset.id} value={asset.id}>
                      {asset.asset_id} - {asset.asset_name} ({asset.asset_type})
                    </option>
                  ))}
                </select>
                {assets.length === 0 && (
                  <p className="text-sm text-orange-600 mt-1">No available assets. All assets are currently assigned.</p>
                )}
              </div>

              {/* Employee Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Select Employee *</label>
                <select
                  value={formData.employee_id}
                  onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Choose an employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.employee_id} - {emp.first_name} {emp.last_name} ({emp.designation})
                    </option>
                  ))}
                </select>
              </div>

              {/* Allocation Date */}
              <div>
                <label className="block text-sm font-medium mb-2">Allocation Date *</label>
                <input
                  type="date"
                  value={formData.allocation_date}
                  onChange={(e) => setFormData({...formData, allocation_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Any additional notes about this allocation..."
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                type="submit"
                disabled={loading || assets.length === 0}
                className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: loading || assets.length === 0 ? '#9e9e9e' : '#2b3380'}}
              >
                <UserPlus className="w-5 h-5" />
                {loading ? 'Allocating...' : 'Allocate Asset'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/assets')}
                className="px-6 py-3 rounded-lg border font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        {/* Available Assets Preview */}
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Available Assets ({assets.length})</h2>
          {assets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {assets.map(asset => (
                <div key={asset.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <Package className="w-8 h-8" style={{color: '#2b3380'}} />
                    <div>
                      <p className="font-bold">{asset.asset_id}</p>
                      <p className="text-sm text-gray-600">{asset.asset_name}</p>
                      <p className="text-xs text-gray-500">{asset.asset_type} - {asset.condition}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No available assets to allocate</p>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default AssetAllocation;